import { RefinementCtx, z } from 'zod/v3';
import {
  errorMap,
  zCodeWithoutLeadingNumber,
  zValidPeriod,
} from '../../../common/utils/schema';
import { CategoryValidFormDataSchema } from './CategoryValidFormDataSchema';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './ClassifierValidFormDataSchema.translations.json';
import { warningMessage } from '../../../common/utils/warningMessage';
import { CategoryHierarchyValidFormDataSchema } from './CategoryHierarchyValidFormDataSchema';
import { zDataType } from '../../../common/utils/schema/zDataType';

const { addTranslations, t } = i18nNsFactory(
  'classifier-valid-form-data-schema',
);
addTranslations(translations);

const BaseClassifierValidFormDataSchema = z.object({
  id: z.number().nullable(),
  code: zCodeWithoutLeadingNumber(),
  name: z.string(errorMap(() => t('validation.name.required'))).min(1),
  description: z.string(),
  validPeriod: zValidPeriod(),
  categories: z.array(CategoryValidFormDataSchema),
  categoryGroups: z.array(CategoryValidFormDataSchema),
  combinedGroups: z.array(CategoryValidFormDataSchema),
  hierarchy: z.array(CategoryHierarchyValidFormDataSchema).nullable(),
});

export const ClassifierValidFormDataSchema = z
  .union([
    BaseClassifierValidFormDataSchema.extend({
      hasCategories: z.literal(true),
      codeFormat: z.literal(''),
      dataTypeId: z.literal(null),
    }),
    BaseClassifierValidFormDataSchema.extend({
      hasCategories: z.literal(false),
      codeFormat: z.string().nullable(),
      dataTypeId: z
        .number(
          errorMap(() => t('validation.dataTypeId.required-if-no-categories')),
        )
        .min(1)
        .nullable(),
      dataTypeCode: zDataType,
    }).superRefine(validateCodeFormat),
  ])
  .superRefine((data, ctx) => {
    const { hasCategories, categories, categoryGroups, combinedGroups } = data;

    if (hasCategories) {
      if (
        categories.length === 0 &&
        categoryGroups.length === 0 &&
        combinedGroups.length === 0
      ) {
        ctx.addIssue({
          code: 'custom',
          message: warningMessage(
            t('validation.hasCategories.warn-no-categories'),
          ),
          path: ['hasCategories'],
        });
      }
    }
  })
  .superRefine((data, ctx) => {
    const { categories, categoryGroups, combinedGroups } = data;
    const addIssue = (path: (string | number)[]) =>
      ctx.addIssue({
        code: 'custom',
        message: t('validation.categories.code.unique'),
        path,
      });

    findDuplicateCodeIndexes(
      ...categories,
      ...categoryGroups,
      ...combinedGroups,
    ).forEach((index) => {
      addIssue(
        index < categories.length
          ? ['categories', index, 'code']
          : index < categories.length + categoryGroups.length
            ? ['categoryGroups', index - categories.length, 'code']
            : [
                'combinedGroups',
                index - categories.length - categoryGroups.length,
                'code',
              ],
      );
    });
  });

const findDuplicateCodeIndexes = (...objects: { code: string }[]): number[] => {
  const cache: string[] = [];
  const duplicateIndexes: number[] = [];

  for (const [index, code] of objects.map(({ code }) => code).entries()) {
    const lowerCaseCode = code.toLowerCase();
    const cacheIndex = cache.indexOf(lowerCaseCode);
    if (cacheIndex !== -1) {
      duplicateIndexes.push(cacheIndex, index);
    }
    cache.push(lowerCaseCode);
  }

  return Array.from(new Set(duplicateIndexes));
};

async function validateCodeFormat(
  {
    hasCategories,
    codeFormat,
    dataTypeCode,
  }: {
    hasCategories: boolean;
    codeFormat: string | null;
    dataTypeCode: 'DATE' | 'NUMBER' | 'TEXT';
  },
  ctx: RefinementCtx,
) {
  let valid = true;

  if (!hasCategories && !codeFormat && dataTypeCode !== 'NUMBER') {
    return ctx.addIssue({
      code: 'custom',
      message: t('validation.codeFormat.required-if-no-categories'),
      path: ['codeFormat'],
    });
  }

  switch (dataTypeCode) {
    case 'DATE': {
      const { isDateFormatValid } = await import(
        '../../../common/utils/parsers/parseDate'
      );
      valid = isDateFormatValid(codeFormat || '');
      break;
    }
    case 'TEXT': {
      const { isCodeFormatValid } = await import('../../isCodeFormatValid');
      valid = isCodeFormatValid(codeFormat || '');
      break;
    }
  }

  if (!valid) {
    ctx.addIssue({
      code: 'custom',
      message: t('validation.codeFormat.invalid'),
      path: ['codeFormat'],
    });
  }
}
